'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { PAGE_SIZE, dedupeById, mapFileToHubItem } from '../../../lib/document-hub-utils';
import { HubItem, Section } from '../../../interfaces';

interface DocumentHubDataHook {
  itemsBySection: Record<string, HubItem[]>;
  loadingBySection: Record<string, boolean>;
  maxVisibleBySection: Record<string, number>;
  noMoreBySection: Record<string, boolean>;
  setMaxVisibleBySection: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  fetchSlice: (section: Section, start: number, end: number) => Promise<HubItem[]>;
  ensureLoaded: (section: Section) => Promise<void>;
  loadMore: (section: Section) => Promise<void>;
  showAll: (section: Section) => Promise<void>;
  totalFor: (section: Section) => number;
  resetData: () => void;
}

export const useDocumentHubData = (
  sections: Section[],
  pageSlug: string | undefined,
  onDataFetched?: (files: any[]) => void,
): DocumentHubDataHook => {
  const [itemsBySection, setItemsBySection] = useState<Record<string, HubItem[]>>({});
  const [loadingBySection, setLoadingBySection] = useState<Record<string, boolean>>({});
  const [noMoreBySection, setNoMoreBySection] = useState<Record<string, boolean>>({});
  const loadedOnceRef = useRef<Set<string>>(new Set());
  const [maxVisibleBySection, setMaxVisibleBySection] = useState<Record<string, number>>({});
  const inflightRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    sections.forEach((section) => {
      if (section.items?.length) {
        setItemsBySection((prev) => {
          const current = prev[section.id] ?? [];
          const hasChanged =
            current.length !== section.items.length ||
            current.some((item, index) => item.id !== section.items[index]?.id);
          if (hasChanged) {
            return { ...prev, [section.id]: dedupeById(section.items) };
          }
          return prev;
        });
        setNoMoreBySection((p) => {
          if (!p[section.id]) {
            return { ...p, [section.id]: true };
          }
          return p;
        });
        loadedOnceRef.current.add(section.id);
      }
    });
  }, [sections]);

  const bumpInflight = useCallback((id: string, d: 1 | -1) => {
    const map = inflightRef.current;
    const next = (map.get(id) ?? 0) + d;
    map.set(id, Math.max(0, next));
    setLoadingBySection((p) => ({ ...p, [id]: next > 0 }));
  }, []);

  const totalFor = useCallback(
    (section: Section) =>
      Number.isFinite(section.count) ? Number(section.count) : (itemsBySection[section.id]?.length ?? 0),
    [itemsBySection],
  );

  const fetchSlice = useCallback(
    async (section: Section, start: number, end: number): Promise<HubItem[]> => {
      if (!pageSlug) return [];
      const total = totalFor(section);
      const cappedStart = Math.min(start, total);
      const cappedEnd = Math.min(end, total);
      if (cappedEnd <= cappedStart) return [];

      bumpInflight(section.id, 1);
      try {
        const qs = new URLSearchParams({
          slug: pageSlug,
          panelKey: section.id,
          start: String(cappedStart),
          end: String(cappedEnd),
        });
        const res = await fetch(`/api/document-panels?${qs.toString()}`, {
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const batch: any[] = json.files ?? [];
        onDataFetched?.(batch);

        if (batch.length < cappedEnd - cappedStart) {
          setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        }

        return batch.map(mapFileToHubItem);
      } finally {
        bumpInflight(section.id, -1);
      }
    },
    [pageSlug, totalFor, onDataFetched, bumpInflight],
  );

  const ensureLoaded = useCallback(
    async (section: Section) => {
      if (loadedOnceRef.current.has(section.id)) return;

      // When items are pre-loaded server-side (e.g. Sanity SSR), skip the API fetch
      if (section.items?.length) {
        setItemsBySection((prev) => ({ ...prev, [section.id]: dedupeById(section.items) }));
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        loadedOnceRef.current.add(section.id);
        return;
      }

      const total = totalFor(section);
      const initialEnd = Math.min(PAGE_SIZE, total || PAGE_SIZE);
      const slice = await fetchSlice(section, 0, initialEnd);
      setItemsBySection((prev) => ({
        ...prev,
        [section.id]: dedupeById(slice),
      }));
      if (slice.length < initialEnd || (total && slice.length >= total)) {
        setNoMoreBySection((p) => ({
          ...p,
          [section.id]: total ? slice.length >= total : false,
        }));
      }
      loadedOnceRef.current.add(section.id);
    },
    [fetchSlice, totalFor],
  );

  const loadMore = useCallback(
    async (section: Section) => {
      const current = itemsBySection[section.id] ?? [];
      const total = totalFor(section);

      setMaxVisibleBySection((p) => ({
        ...p,
        [section.id]: (p[section.id] ?? PAGE_SIZE) + PAGE_SIZE,
      }));

      if (noMoreBySection[section.id] || (total && current.length >= total)) {
        if (total && current.length >= total) {
          setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        }
        return;
      }
      const nextStart = current.length;
      const nextEnd = Math.min(nextStart + PAGE_SIZE, total || nextStart + PAGE_SIZE);

      const slice = await fetchSlice(section, nextStart, nextEnd);
      if (slice.length === 0) {
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        return;
      }
      setItemsBySection((p) => {
        const merged = dedupeById([...(p[section.id] ?? []), ...slice]);
        return { ...p, [section.id]: merged };
      });
      if (total && nextEnd >= total) setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
    },
    [itemsBySection, fetchSlice, totalFor, noMoreBySection],
  );

  const showAll = useCallback(
    async (section: Section) => {
      const current = itemsBySection[section.id] ?? [];
      const total = totalFor(section);

      setMaxVisibleBySection((p) => ({
        ...p,
        [section.id]: total || current.length,
      }));

      if (noMoreBySection[section.id] || (total && current.length >= total)) {
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        return;
      }

      const slice = await fetchSlice(section, current.length, total || current.length);
      setItemsBySection((p) => {
        const merged = dedupeById([...(p[section.id] ?? []), ...slice]);
        return { ...p, [section.id]: merged };
      });
      setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
    },
    [itemsBySection, fetchSlice, totalFor, noMoreBySection],
  );

  const resetData = useCallback(() => {
    setItemsBySection({});
    setLoadingBySection({});
    setNoMoreBySection({});
    loadedOnceRef.current = new Set();
    inflightRef.current = new Map();
    const init: Record<string, number> = {};
    sections.forEach((s) => (init[s.id] = PAGE_SIZE));
    setMaxVisibleBySection(init);
  }, [sections]);

  return {
    itemsBySection,
    loadingBySection,
    maxVisibleBySection,
    noMoreBySection,
    setMaxVisibleBySection,
    fetchSlice,
    ensureLoaded,
    loadMore,
    showAll,
    totalFor,
    resetData,
  };
};
